import { useState, useCallback } from 'react';
import { fetchPincodeData } from '../services/pincodeService';
import { Country, State } from 'country-state-city';

/**
 * Custom hook to handle pincode autofill functionality
 * Fetches location data based on pincode and auto-fills form fields
 * 
 * @param {Function} setFormData - Function to update form data
 * @param {Object} fieldMapping - Optional custom field name mapping
 * @returns {Object} - Hook utilities and state
 * 
 * @example
 * const { handlePincodeChange, loading, error, locationData } = usePincodeAutofill(setFormData);
 * 
 * // With custom field mapping
 * const { handlePincodeChange } = usePincodeAutofill(setFormData, {
 *   city: 'customCityField',
 *   state: 'customStateField',
 *   taluk: 'customTalukField',
 *   village: 'customVillageField'
 * });
 */
export const usePincodeAutofill = (setFormData, fieldMapping = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locationData, setLocationData] = useState(null);

  // Default field mapping
  const defaultFieldMapping = {
    pincode: 'postelCode',
    city: 'city',
    state: 'state',
    country: 'country',
    district: 'district',
    taluk: 'taluk',
    village: 'villageTown',
    area: 'address',
    region: 'address1',
    division: 'address2',
  };

  // Merge default mapping with custom mapping
  const fields = { ...defaultFieldMapping, ...fieldMapping };

  /**
   * Handle pincode change and fetch location data
   * @param {string} pincode - The entered pincode
   * @param {string} countryCode - Optional country code (default: 'IN')
   */
  const handlePincodeChange = useCallback(async (pincode, countryCode = 'IN') => {
    // Clear previous error
    setError(null);

    // Validate pincode length (6 digits for India)
    if (countryCode === 'IN' && pincode.length !== 6) {
      if (pincode.length > 0 && pincode.length < 6) {
        setError('Pincode must be 6 digits');
      }
      return;
    }

    // Fetch data only if pincode is complete
    if ((countryCode === 'IN' && pincode.length === 6) || 
        (countryCode !== 'IN' && pincode.length >= 4)) {
      setLoading(true);
      
      try {
        const data = await fetchPincodeData(pincode, countryCode);
        
        if (data.success) {
          setLocationData(data);
          
          // Convert string values to objects for country and state
          let countryObj = data.country;
          let stateObj = data.state;
          
          // If country is a string, try to find the matching country object
          if (typeof data.country === 'string') {
            const foundCountry = Country.getAllCountries().find(
              c => c.name.toLowerCase() === data.country.toLowerCase() || 
                   c.isoCode.toLowerCase() === data.country.toLowerCase()
            );
            countryObj = foundCountry || data.country;
          }
          
          // If state is a string, try to find the matching state object
          if (typeof data.state === 'string' && typeof countryObj === 'object') {
            const foundState = State.getStatesOfCountry(countryObj.isoCode).find(
              s => s.name.toLowerCase() === data.state.toLowerCase() ||
                   s.isoCode.toLowerCase() === data.state.toLowerCase()
            );
            stateObj = foundState || data.state;
          }
          
          // Auto-fill form fields (excluding village/town - will be dropdown, and address field)
          setFormData((prevData) => ({
            ...prevData,
            [fields.city]: data.city || data.district || '',
            [fields.state]: stateObj || '',
            [fields.country]: countryObj || 'India',
            [fields.district]: data.district || data.city || '',
            [fields.taluk]: data.taluk || data.block || '',
            // Village/Town will be set separately via dropdown selection
            // Don't auto-fill address field
          }));
          
          setError(null);
        } else {
          setError(data.error || 'Invalid pincode');
          setLocationData(null);
        }
      } catch (err) {
        setError('Failed to fetch location data');
        setLocationData(null);
      } finally {
        setLoading(false);
      }
    }
  }, [setFormData, fields]);

  /**
   * Clear location data and errors
   */
  const clearLocationData = useCallback(() => {
    setLocationData(null);
    setError(null);
  }, []);

  /**
   * Get available villages/areas for selection
   * @returns {Array} - Array of village/area options
   */
  const getVillageOptions = useCallback(() => {
    if (!locationData || !locationData.allPostOffices) {
      return [];
    }
    
    // Extract unique villages from all post offices
    const villages = locationData.allPostOffices
      .map(po => po.village || po.region || po.name)
      .filter((value, index, self) => value && self.indexOf(value) === index);
    
    return villages;
  }, [locationData]);

  /**
   * Get available taluk/block options
   * @returns {Array} - Array of taluk/block options
   */
  const getTalukOptions = useCallback(() => {
    if (!locationData || !locationData.allPostOffices) {
      return [];
    }
    
    // Extract unique taluks from all post offices
    const taluks = locationData.allPostOffices
      .map(po => po.taluk || po.block)
      .filter((value, index, self) => value && self.indexOf(value) === index);
    
    return taluks;
  }, [locationData]);

  return {
    handlePincodeChange,
    clearLocationData,
    getVillageOptions,
    getTalukOptions,
    loading,
    error,
    locationData,
    isDataAvailable: locationData !== null
  };
};

export default usePincodeAutofill;
